import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import { FaChevronDown, FaChevronUp, FaCheck } from "react-icons/fa6";
import "./SearchableDropdown.css";

function SearchableDropdown({
  options = [],
  value,
  onChange,
  getOptionValue = (option) => option?.value,
  getOptionLabel = (option) => option?.label,
  placeholder = "Select",
  searchPlaceholder = "Search...",
  disabled = false,
  className = "",
  triggerClassName = "",
  noResultsText = "No options found",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = useMemo(
    () =>
      options.find((option) => getOptionValue(option) === value) || null,
    [getOptionValue, options, value],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      (getOptionLabel(option) || "")
        .toString()
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [getOptionLabel, options, searchQuery]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const toggleDropdown = () => {
    if (disabled) return;

    setIsOpen((prev) => !prev);
    setSearchQuery("");
  };

  const handleSelect = (option) => {
    if (typeof onChange === "function") {
      onChange(option);
    }

    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div
      className={`searchable-dropdown ${className} ${
        isOpen ? "is-open" : ""
      } ${disabled ? "is-disabled" : ""}`}
      ref={containerRef}
    >
      <button
        type="button"
        className={`fy-filter-select searchable-dropdown-trigger ${triggerClassName}`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span
          className={`searchable-dropdown-trigger-text ${
            selectedOption ? "" : "is-placeholder"
          }`}
        >
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <span className="searchable-dropdown-trigger-icon" aria-hidden="true">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {isOpen && (
        <div className="searchable-dropdown-menu">
          <div className="searchable-dropdown-search">
            <IoIosSearch className="searchable-dropdown-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="searchable-dropdown-search-input"
              placeholder={searchPlaceholder}
            />
          </div>

          <div className="searchable-dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const isSelected = optionValue === value;

                return (
                  <button
                    type="button"
                    key={String(optionValue)}
                    className={`searchable-dropdown-option ${
                      isSelected ? "is-selected" : ""
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="searchable-dropdown-option-label">
                      {getOptionLabel(option)}
                    </span>
                    {isSelected && (
                      <FaCheck
                        className="searchable-dropdown-option-check"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="searchable-dropdown-empty">{noResultsText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
